import { useState } from 'react';

import { Alert, Avatar, Badge, Button, useConfirmDialog } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
// B2 Lote 3 #23: lookup leve (funciona p/ não-admin) → nome + foto do autor e @menções.
import { useUsersLookup } from '@/lib/admin-store';
import {
  canEditInternalNote,
  useInternalNotes,
  useInternalNotesActions,
  type InternalNote,
} from '@/lib/internal-notes-store';
// UX-4 / BUG-V2-026: paridade mínima com comentários — markdown render + emoji shortcuts.
import { renderMarkdown } from '@/lib/render-markdown';
// UI-6 / TD-V2-NOTAS-01: editor com @mention autocomplete + emoji shortcuts.
import { InternalNoteEditor } from './InternalNoteEditor';
import { resolveMentions } from '@/lib/mentions';

import { formatDataHoraSegundos } from '@/lib/format-date';
/**
 * <InternalNotesTab> — aba 🔒 "Notas Internas" do card (FEAT-V2-009, Bloco C).
 *
 * Renderiza:
 *   - Tag de aviso destacada ("Não visível pro cliente externo")
 *   - Lista de notas (ordem ASC por criado_em_iso) com autor + timestamp + body
 *   - Editor simples (textarea) com botão "Adicionar nota"
 *   - Ícone lápis ✏️ dentro de 5min pra editar inline (só autor)
 *   - Ícone lixeira 🗑️ — autor sempre OU admin
 *
 * Visibilidade controlada pelo CONSUMIDOR (DealDetailDialog etc) via
 * `useCanSeeInternalNotes(workflowSlug)`. Este componente assume que já
 * passou o gate de permissão.
 *
 * Feature flag `NOTAS_INTERNAS_ENABLED` enforced no backend (retorna 503
 * quando OFF). UI mostra mensagem de erro amigável neste caso.
 */

interface InternalNotesTabProps {
  cardId: string;
  workflowSlug: string;
}

export function InternalNotesTab({ cardId, workflowSlug }: InternalNotesTabProps) {
  const { user } = useAuth();
  const { data: notes = [], isLoading, error } = useInternalNotes(cardId, workflowSlug);
  const actions = useInternalNotesActions(cardId, workflowSlug);
  const users = useUsersLookup();
  // FASE 2 / M1: ConfirmDialog do design system (substitui confirm() nativo)
  const confirm = useConfirmDialog();
  const [newNoteBody, setNewNoteBody] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  // UI-6 / TD-V2-NOTAS-01: pre-fill editor com quote da nota replyTo.
  function handleReply(note: InternalNote) {
    const autor = users.find((u) => u.id === note.autorId);
    const autorRef = autor ? `@${autor.id}` : '';
    const quoted = note.body
      .split('\n')
      .map((line) => `> ${line}`)
      .join('\n');
    setNewNoteBody((prev) => {
      const prefix = prev.trim() ? `${prev}\n\n` : '';
      return `${prefix}${autorRef} disse:\n${quoted}\n\n`;
    });
    // Foca o editor pra usuário continuar digitando
    requestAnimationFrame(() => {
      const ta = document.querySelector<HTMLTextAreaElement>(
        '[data-testid="internal-note-body"]',
      );
      ta?.focus();
      if (ta) ta.selectionStart = ta.selectionEnd = ta.value.length;
    });
  }

  async function handleAdd() {
    const body = newNoteBody.trim();
    if (!body) {
      setAddError('Digite o conteúdo da nota');
      return;
    }
    setAddError(null);
    try {
      await actions.addNote(body);
      setNewNoteBody('');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erro ao adicionar nota';
      if (msg.includes('feature-disabled')) {
        setAddError('Notas internas estão desativadas. Pergunte ao admin.');
      } else if (msg.includes('forbidden-not-in-department')) {
        setAddError('Você não tem permissão pra adicionar notas neste card.');
      } else {
        setAddError(msg);
      }
    }
  }

  if (error) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes('feature-disabled')) {
      return (
        <Alert variant="warning" rail>
          Notas internas estão desativadas (feature flag OFF).
        </Alert>
      );
    }
    if (msg.includes('forbidden')) {
      return (
        <Alert variant="warning" rail>
          Você não tem permissão pra ver notas internas deste card.
        </Alert>
      );
    }
    return <Alert variant="danger" rail>Erro ao carregar notas: {msg}</Alert>;
  }

  return (
    <div className="space-y-4" data-testid="internal-notes-tab">
      {/* Tag visual destacada */}
      <Alert variant="warning" rail>
        🔒 <strong>Notas internas</strong> — visíveis apenas pra equipe interna.
        Não aparecem em nenhum endpoint público nem na visualização do cliente externo.
      </Alert>

      {/* Lista de notas */}
      {isLoading ? (
        <p className="text-sm text-ink-3 italic">Carregando notas…</p>
      ) : notes.length === 0 ? (
        <p className="text-sm text-ink-3 italic">Nenhuma nota interna ainda.</p>
      ) : (
        <ul className="space-y-3">
          {notes.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              currentUserId={user?.id ?? ''}
              isAdmin={user?.isAdmin ?? false}
              onEdit={(newBody) => actions.editNote(note.id, newBody)}
              onDelete={async () => {
                const ok = await confirm({
                  title: 'Apagar nota interna?',
                  message: 'A ação preserva audit mas oculta da listagem.',
                  confirmLabel: 'Apagar',
                  variant: 'destructive',
                });
                if (ok) void actions.deleteNote(note.id);
              }}
              onReply={() => handleReply(note)}
              pending={actions.pending}
            />
          ))}
        </ul>
      )}

      {/* Editor — UI-6 com @mention autocomplete + emoji shortcuts */}
      <div className="space-y-2 pt-2 border-t border-line dark:border-line-dark">
        {addError && <Alert variant="danger" rail>{addError}</Alert>}
        <label htmlFor="new-internal-note" className="block text-xs uppercase tracking-wider text-ink-3">
          Adicionar nota
        </label>
        <InternalNoteEditor
          value={newNoteBody}
          onChange={setNewNoteBody}
          placeholder="Detalhes que só a equipe interna deve ver…  Use @ pra mencionar alguém + :smile: pra emojis."
          testid="internal-note-body"
          rows={3}
        />
        <Button
          type="button"
          size="sm"
          onClick={handleAdd}
          loading={actions.pending === 'add'}
          data-testid="internal-note-add-btn"
        >
          + Adicionar nota
        </Button>
      </div>
    </div>
  );
}

// ─── NoteRow ────────────────────────────────────────────────────────────

interface NoteRowProps {
  note: InternalNote;
  currentUserId: string;
  isAdmin: boolean;
  onEdit: (newBody: string) => Promise<unknown>;
  onDelete: () => void;
  onReply: () => void;
  pending: string | null;
}

function NoteRow({ note, currentUserId, isAdmin, onEdit, onDelete, onReply, pending }: NoteRowProps) {
  const users = useUsersLookup();
  const autor = users.find((u) => u.id === note.autorId);
  const autorNome = autor?.nome ?? note.autorId;
  const canEdit = canEditInternalNote(note, currentUserId);
  const canDelete = note.autorId === currentUserId || isAdmin;
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(note.body);
  const [editError, setEditError] = useState<string | null>(null);
  // UI-6 / TD-V2-NOTAS-01: resolve @user-id → @Nome no body antes do markdown
  // render. Lookup map é caro só na primeira chamada (users é pequeno).
  const mentionResolved = resolveMentions(
    note.body,
    users.map((u) => ({ id: u.id, nome: u.nome })),
  );

  async function handleSaveEdit() {
    const trimmed = editBody.trim();
    if (!trimmed) {
      setEditError('Corpo vazio');
      return;
    }
    setEditError(null);
    try {
      await onEdit(trimmed);
      setEditing(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erro ao editar';
      if (msg.includes('edit-window-expired')) {
        setEditError('Janela de edição expirou (5min após criação).');
      } else {
        setEditError(msg);
      }
    }
  }

  return (
    <li
      className="border border-line dark:border-line-dark rounded-lg p-3"
      data-testid={`internal-note-${note.id}`}
    >
      <div className="flex items-start gap-2 mb-1.5">
        <Avatar name={autorNome} src={autor?.fotoUrl ?? undefined} size="xs" />
        <div className="flex-1 min-w-0">
          <div className="text-xs text-ink-2 dark:text-ink-dark-2">
            <span className="font-medium text-ink-1 dark:text-ink-dark-1">{autorNome}</span>{' '}
            · {formatDataHoraSegundos(note.criadoEmIso)}
            {note.editadoEmIso && (
              <Badge variant="neutral" size="sm" className="ml-2">
                editada
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {/* UI-6 / TD-V2-NOTAS-03: botão de reply — quote no editor de nova nota. */}
          {!editing && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onReply}
              data-testid={`internal-note-reply-${note.id}`}
              aria-label="Responder"
              title="Responder (quote no editor)"
            >
              ↩️
            </Button>
          )}
          {canEdit && !editing && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditBody(note.body);
                setEditing(true);
              }}
              data-testid={`internal-note-edit-${note.id}`}
              aria-label="Editar"
              title="Editar (janela 5min)"
            >
              ✏️
            </Button>
          )}
          {canDelete && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDelete}
              loading={pending === note.id}
              data-testid={`internal-note-delete-${note.id}`}
              aria-label="Excluir"
              title="Excluir"
            >
              🗑️
            </Button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-2 mt-2">
          {editError && <Alert variant="danger" rail>{editError}</Alert>}
          {/* UI-6: mesmo editor com autocomplete + emoji shortcuts no edit inline */}
          <InternalNoteEditor
            value={editBody}
            onChange={setEditBody}
            testid={`internal-note-edit-editor-${note.id}`}
            rows={3}
            autoFocus
          />
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={handleSaveEdit} loading={pending === note.id}>
              Salvar
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        // UX-4 + UI-6: markdown sanitizado (renderMarkdown usa DOMPurify, XSS-safe)
        // + resolveMentions transforma @user-id em @Nome ANTES do markdown render.
        <div
          className="prose prose-sm dark:prose-invert text-sm text-ink-1 dark:text-ink-dark-1 ml-7 max-w-none"
          data-testid={`internal-note-body-${note.id}`}
          dangerouslySetInnerHTML={{ __html: renderMarkdown(mentionResolved) }}
        />
      )}
    </li>
  );
}
